import { morphoPools, morphoTypeMarkets } from '@1delta/data-sdk'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { MORPHO_LENS } from '../../user-data/morpho/userCallBuild'
import { getListaMarketDataConverter } from './getMarketsFromChain'
import { fetchListaFixedTerms } from './listaBroker'
import { getEvmClient } from '@1delta/providers'
import { parseAbi } from 'viem'
import lodash from 'lodash'

const { chunk } = lodash
export const LISTA_MARKET_CHUNK_SIZE = 100

const listaLensAbi = parseAbi([
  'function getListaMarketDataCompact(address morpho, bytes32[] calldata marketsIds) external view returns (bytes memory data)',
])

export async function fetchListaPublicData(
  chainId: string,
  yields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  prices: Record<string, number> = {},
  tokens: GenericTokenList = {},
) {
  const markets = morphoTypeMarkets()?.LISTA_DAO?.[chainId] ?? []
  const mb = morphoPools()?.LISTA_DAO?.[chainId]
  if (!mb || markets.length === 0) return {}

  const lensAddress = MORPHO_LENS[chainId]
  if (!lensAddress) return {}

  const provider = getEvmClient(chainId)
  const marketChunks = chunk(markets, LISTA_MARKET_CHUNK_SIZE)

  // Warm the fixed-term cache so the (sync) converter can attach the broker menu.
  // Best-effort: brokered markets only; failures leave fixedTerms undefined.
  const fixedTermsPromise = fetchListaFixedTerms(chainId, markets).catch(() => ({}))

  // Fetch raw bytes per chunk — same contract function as the cron multicall path
  const rawResults = await Promise.all(
    marketChunks.map(async (marketGroup) => {
      const result = await provider.simulateContract({
        abi: listaLensAbi,
        functionName: 'getListaMarketDataCompact',
        address: lensAddress as any,
        args: [mb, marketGroup] as any,
      })
      return result.result ?? '0x'
    }),
  )

  // Ensure the fixed-term cache is populated before the converter reads it
  await fixedTermsPromise

  // Reuse the same converter the cron path uses
  const [converter] = getListaMarketDataConverter(
    'LISTA_DAO',
    chainId,
    prices,
    yields,
    tokens,
  )
  return converter(rawResults)
}

export function buildListaCall(chainId: string, marketsOverride?: string[]) {
  const markets =
    marketsOverride ?? morphoTypeMarkets().LISTA_DAO?.[chainId] ?? []
  const mb = morphoPools()?.LISTA_DAO?.[chainId]
  if (!mb || markets.length === 0) return []

  const marketChunks = chunk(markets, LISTA_MARKET_CHUNK_SIZE)
  return marketChunks.map((marketGroup) => ({
    address: MORPHO_LENS[chainId],
    name: 'getListaMarketDataCompact',
    params: [mb, marketGroup],
  }))
}
